import { useState, useEffect } from 'react';
import Layout from '../../components/feature/Layout';
import { useReveal } from '../../hooks/useReveal';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase, Partner } from '../../lib/supabase';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const partnersSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/partners`,
  url: `${siteUrl}/partners`,
  name: 'Strategic Partners | KV Trade Alliance',
  description:
    '1,240+ corporate partners across Korea and Vietnam. Join the premier bilateral trade network spanning government agencies, Fortune 500 firms, and growing SMEs.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Partners', item: `${siteUrl}/partners` },
    ],
  },
  mainEntity: {
    '@type': 'Organization',
    name: 'KV Trade Alliance',
    description: 'Korea-Vietnam bilateral trade agency with 1,240+ partner companies.',
  },
};

function getLocalizedSector(partner: Partner, lang: string): string {
  const i18n = partner.sector_i18n;
  if (!i18n) return partner.sector;
  return (i18n as Record<string, string>)[lang] || i18n.ko || partner.sector;
}

export default function PartnersPage() {
  const ref = useReveal();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('partners').select('*').order('country').order('sort_order')
      .then(({ data, error }) => {
        if (error) {
          console.warn('[Partners] Supabase error:', error.message);
        } else if (data) {
          setPartners(data as Partner[]);
        }
        setLoading(false);
      });
  }, []);

  return (
    <Layout>
      <title>{t('page.title.partners')}</title>
      <meta
        name="description"
        content="1,240+ corporate partners across Korea and Vietnam. Join KV Trade Alliance's premier bilateral trade network — government agencies, Fortune 500 companies, and SMEs."
      />
      <meta
        name="keywords"
        content="KV Trade Alliance partners, Korea Vietnam trade network, bilateral trade partners, Korean Vietnamese business alliance"
      />
      <link rel="canonical" href={`${siteUrl}/partners`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(partnersSchema) }}
      />
      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-24 bg-navy overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/606de06df85f774bbccde0da338e1e07.jpg"
            alt="Our Partners"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/90 to-navy/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <p className="text-xs font-sans text-silver/50 tracking-[0.2em] uppercase mb-4">{t('partnersPage.eyebrow')}</p>
          <h1 className="font-serif text-white text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight mb-4 lg:mb-5 max-w-2xl">
            {t('partnersPage.heading1')}
          </h1>
          <p className="text-white/60 font-sans text-base lg:text-lg max-w-xl leading-relaxed">
            {t('partnersPage.heroDesc')}
          </p>
        </div>
      </section>

      {/* Partner Grid */}
      <section ref={ref} className="bg-white py-14 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal text-center mb-10 lg:mb-16">
            <p className="text-xs font-sans text-navy/40 tracking-[0.2em] uppercase mb-3">{t('partnersPage.section.eyebrow')}</p>
            <h2 className="font-serif text-navy text-2xl lg:text-4xl font-bold mb-2">{t('partnersPage.section.heading')}</h2>
            <p className="text-navy/50 font-sans text-sm lg:text-base italic">{t('partnersPage.section.sub')}</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-navy/20 border-t-navy rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Korea Partners */}
              <div className="mb-10 lg:mb-12">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#CD2E3A] rounded-sm flex-shrink-0">
                    <span className="text-white text-xs font-bold">KR</span>
                  </div>
                  <h3 className="font-serif text-navy text-lg lg:text-xl font-semibold whitespace-nowrap">{t('partnersPage.kr.label')}</h3>
                  <div className="flex-1 h-px bg-silver-light" />
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 lg:gap-4">
                  {partners.filter(p => p.country === 'Korea').map((p, i) => (
                    <div
                      key={p.id}
                      className={`reveal stagger-${(i % 6) + 1} bg-[#F8F9FB] rounded-xl p-4 lg:p-6 flex flex-col items-center gap-2 lg:gap-3 border border-transparent hover:border-silver hover:bg-white transition-all duration-300 cursor-default`}
                    >
                      <div className="w-10 h-10 lg:w-14 lg:h-14 flex items-center justify-center rounded-full flex-shrink-0 overflow-hidden bg-white">
                        {p.logo_url && p.logo_url.trim() !== '' ? (
                          <img src={p.logo_url} alt={p.name} className="w-full h-full object-contain p-1" />
                        ) : (
                          <div
                            className="w-full h-full flex items-center justify-center rounded-full"
                            style={{ backgroundColor: p.color }}
                          >
                            <span className="text-white text-sm lg:text-base font-serif font-bold tracking-wide">{p.initials}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-center">
                        <p className="text-navy text-[10px] lg:text-xs font-sans font-semibold leading-tight">{p.name}</p>
                        <p className="text-navy/40 text-[9px] lg:text-[10px] font-sans mt-1">{getLocalizedSector(p, lang)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vietnam Partners */}
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#DA251D] rounded-sm flex-shrink-0">
                    <span className="text-[#FFC900] text-xs font-bold">VN</span>
                  </div>
                  <h3 className="font-serif text-navy text-lg lg:text-xl font-semibold whitespace-nowrap">{t('partnersPage.vn.label')}</h3>
                  <div className="flex-1 h-px bg-silver-light" />
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 lg:gap-4">
                  {partners.filter(p => p.country === 'Vietnam').map((p, i) => (
                    <div
                      key={p.id}
                      className={`reveal stagger-${(i % 6) + 1} bg-[#F8F9FB] rounded-xl p-4 lg:p-6 flex flex-col items-center gap-2 lg:gap-3 border border-transparent hover:border-silver hover:bg-white transition-all duration-300 cursor-default`}
                    >
                      <div className="w-10 h-10 lg:w-14 lg:h-14 flex items-center justify-center rounded-full flex-shrink-0 overflow-hidden bg-white">
                        {p.logo_url && p.logo_url.trim() !== '' ? (
                          <img src={p.logo_url} alt={p.name} className="w-full h-full object-contain p-1" />
                        ) : (
                          <div
                            className="w-full h-full flex items-center justify-center rounded-full"
                            style={{ backgroundColor: p.color }}
                          >
                            <span className="text-white text-sm lg:text-base font-serif font-bold tracking-wide">{p.initials}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-center">
                        <p className="text-navy text-[10px] lg:text-xs font-sans font-semibold leading-tight">{p.name}</p>
                        <p className="text-navy/40 text-[9px] lg:text-[10px] font-sans mt-1">{getLocalizedSector(p, lang)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Testimonials - removed */}

      {/* Become a Partner CTA */}
      <section className="bg-navy py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs font-sans text-silver/50 tracking-[0.2em] uppercase mb-4">{t('partnersPage.cta.eyebrow')}</p>
          <h2 className="font-serif text-white text-2xl lg:text-4xl font-bold mb-4">{t('partnersPage.cta.heading')}</h2>
          <p className="text-white/60 font-sans text-sm lg:text-base mb-8 leading-relaxed">
            {t('partnersPage.cta.desc')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-navy text-sm font-sans font-semibold px-8 py-4 rounded hover:bg-silver-light transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            {t('partnersPage.cta.btn')} <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
